import React from "react";
import PolicyPage from "../../components/policy/PolicyPage";
import { BUSINESS } from "../../config/business";

export default function WarrantyReturns() {
  const sections = [
    {
      heading: "Our position on returns, in plain terms",
      body: [
        {
          note:
            "We do not accept returns or exchanges for change of mind. Once a product has been handed over to you, it cannot be returned because it no longer sells as new. What you get instead is the manufacturer's warranty, plus our own service support — set out below.",
        },
        `An electric bicycle is a registered, serial-numbered, road-used product. The moment it leaves the showroom the battery has been cycled, the frame has been ridden and the unit can no longer be sold as new to another customer. That is why we, like other e-cycle dealers, cannot operate an open return window.`,
        `We would much rather you buy the right product than return the wrong one. Come and test-ride before you buy — every model we stock is available to try at our showroom at ${BUSINESS.operatingAddress}, free and without obligation. Our staff will size the frame to you and talk through range honestly.`,
      ],
    },
    {
      heading: "The two exceptions",
      body: [
        `There are exactly two situations in which a product goes back:`,
        {
          list: [
            "Transit damage or a wrong item — reported at delivery, or within 24 hours of handover at the latest, with photographs. We repair or replace at our cost. See section 3.",
            "A manufacturing defect — handled as a warranty claim under section 4 onwards, for the full duration of the manufacturer's warranty.",
          ],
        },
        `Everything else — the colour looks different in daylight, the range is less than you hoped in winter, a family member does not like it, you found it cheaper elsewhere — is not a return.`,
      ],
    },
    {
      heading: "Damaged on arrival, or wrong item",
      body: [
        `Check the product thoroughly before you sign the handover record. Our staff will do it with you.`,
        {
          table: {
            head: ["When reported", "What happens"],
            rows: [
              [
                "At the moment of delivery, before signing",
                "Do not accept the item. We take it back on the spot and replace it, or refund in full if no replacement is available",
              ],
              [
                "Within 24 hours of handover, with photographs or unboxing video",
                "We collect the item at our cost, verify, and replace or repair it free of charge. Where no replacement is possible, we refund in full",
              ],
              [
                "After 24 hours",
                "Cannot be accepted as transit damage, because it is not possible to distinguish it from damage occurring after handover. Any underlying manufacturing fault remains covered under warranty",
              ],
            ],
          },
        },
        `To report, call ${BUSINESS.phone} or email ${BUSINESS.email} with your invoice number and clear photographs of the damage and the packaging.`,
      ],
    },
    {
      heading: "Manufacturer warranty — what is covered",
      body: [
        `Every new product we sell carries the warranty offered by its manufacturer. The warranty is honoured through us as an authorised dealer, so you deal with us, not with a distant service centre.`,
        `Warranty periods vary by brand, model and component. The exact period for your product is printed on the warranty card issued with your invoice, and that card prevails over the indicative table below.`,
        {
          table: {
            head: ["Component", "Typical warranty period", "Notes"],
            rows: [
              [
                "Frame",
                "Commonly 1 to 3 years, model dependent",
                "Against manufacturing defect in material or welding",
              ],
              [
                "Battery (lithium-ion pack)",
                "Commonly 1 to 3 years, model dependent",
                "Against manufacturing defect and abnormal capacity loss. Gradual capacity reduction with normal use and age is not a defect",
              ],
              [
                "Motor",
                "Commonly 1 year",
                "Against manufacturing defect",
              ],
              [
                "Controller, display, charger, sensors",
                "Commonly 6 months to 1 year",
                "Against manufacturing defect",
              ],
              [
                "Accessories and consumables",
                "As stated by the accessory manufacturer, if any",
                "Many accessories carry no warranty",
              ],
            ],
          },
        },
        `A warranty claim is settled by repair, by replacement of the defective part, or — where the manufacturer so determines — by replacement of the product. The manufacturer decides the remedy. A warranty does not entitle you to a refund of the purchase price.`,
      ],
    },
    {
      heading: "What the warranty does not cover",
      body: [
        {
          list: [
            "Normal wear and tear, and consumable parts — tyres, tubes, brake pads, brake and gear cables, chain, sprockets, bearings, grips, saddle, paint finish and decals.",
            "Gradual, expected reduction in battery capacity over time and charge cycles.",
            "Damage from accident, collision, fall, misuse, overloading beyond the rated weight limit, stunts, racing, or commercial or rental use where the product is sold for personal use.",
            "Damage from water ingress caused by immersion, pressure washing or riding through flood water.",
            "Damage from neglect — failure to service, riding with worn brakes, riding a damaged frame, or ignoring a fault warning on the display.",
            "Damage caused by using a charger other than the one supplied, by an unauthorised battery, or by charging in a manner contrary to the manual.",
            "Any product that has been modified, tuned for higher speed or power, or repaired by an unauthorised person or workshop.",
            "Corrosion or rust arising from storage in damp conditions or from failure to clean and dry the vehicle.",
            "Cosmetic marks, scratches and scuffs occurring after handover.",
            "Theft, loss, fire, and damage from natural disaster — these are insurance matters, not warranty matters.",
            "Any product where the frame number or battery serial number has been removed, altered or defaced, or where the invoice cannot be produced.",
          ],
        },
        `Range figures published by manufacturers are measured under controlled test conditions. Real-world range varies with rider weight, terrain, tyre pressure, wind, temperature, assist level and battery age. A shortfall against the published figure is not a defect.`,
      ],
    },
    {
      heading: "How to make a warranty claim",
      body: [
        {
          list: [
            "Contact us on " + BUSINESS.phone + " or at " + BUSINESS.email + " as soon as the fault appears, and describe what is happening. Continuing to ride with a known fault can turn a covered defect into excluded consequential damage.",
            "Have ready your invoice, your warranty card, and the frame and battery serial numbers.",
            "Bring the product to our showroom, or ask us about a pickup where the vehicle is not rideable. Pickup within Patna may be free or chargeable depending on the fault and the distance — we will tell you before we collect.",
            "We inspect the product and, where required, raise the claim with the manufacturer and send diagnostic evidence.",
            "We tell you the outcome and the expected turnaround once the manufacturer responds.",
          ],
        },
        {
          table: {
            head: ["Stage", "Typical time"],
            rows: [
              ["Acknowledgement of your claim", "Within 48 working hours"],
              ["Initial inspection at our workshop", "2 to 5 working days"],
              [
                "Repair where the part is in stock",
                "3 to 7 working days from inspection",
              ],
              [
                "Repair requiring a part from the manufacturer",
                "10 to 30 working days, depending on the manufacturer's despatch",
              ],
              [
                "Battery or motor replacement approved by the manufacturer",
                "15 to 45 working days",
              ],
            ],
          },
        },
        `If inspection shows the fault is not covered by warranty, we will tell you before doing any work, give you a written estimate for a chargeable repair, and let you decide whether to proceed.`,
      ],
    },
    {
      heading: "Components and accessories bought separately",
      body: [
        {
          list: [
            "Unused accessories in sealed, unopened original packaging may be exchanged within 7 days of purchase against production of the invoice. Exchange only — no cash refund.",
            "Electrical components — controllers, displays, sensors, chargers, motors and batteries — cannot be exchanged once the packaging is opened or the part has been fitted, because they cannot be resold or verified as unused. Manufacturing defects remain covered under warranty.",
            "Helmets, safety gear, grips, saddles and any item that has been in contact with a rider cannot be returned or exchanged once used, for hygiene and safety reasons.",
            "A part fitted by anyone other than us, or fitted to a vehicle it was not specified for, is not covered.",
          ],
        },
      ],
    },
    {
      heading: "Battery care — this protects your warranty",
      body: [
        `Most battery claims we see are avoidable. A few habits keep the pack healthy and the warranty intact:`,
        {
          list: [
            "Use only the charger supplied with the product. A third-party charger can damage the pack and voids the warranty.",
            "Do not leave the battery charging unattended, and do not charge it overnight.",
            "Charge in a dry, ventilated place, away from beds, furnishings and exits. Do not charge in direct sunlight or near a heat source.",
            "Avoid running the pack to zero repeatedly. Top up before it is fully flat.",
            "If storing the vehicle for a month or more, store the battery at roughly half charge and top it up every 6 to 8 weeks.",
            "Do not open, puncture, drop or immerse a battery pack. If a pack becomes hot, swollen, or smells unusual, stop using it, move it away from anything flammable and contact us immediately.",
          ],
        },
      ],
    },
    {
      heading: "Servicing",
      body: [
        `We service electric bicycles whether or not you bought them from us, subject to parts availability. Service work is quoted before it starts and invoiced only for what was done.`,
        {
          list: [
            "Workmanship on a chargeable repair carried out by us is warranted for 30 days on that specific repair.",
            "Parts fitted by us carry the part manufacturer's warranty, where one exists.",
            "Free service coupons issued with a new purchase, where applicable, are valid at our showroom within the periods stated on the coupon and cover labour only, not consumables.",
            "Cancellation of a service or repair job is dealt with in our Refund & Cancellation Policy.",
          ],
        },
      ],
    },
    {
      heading: "Your statutory rights",
      body: [
        `This policy does not limit your rights under the Consumer Protection Act, 2019 or any other law that cannot be excluded by agreement. Your right to a remedy for goods that are defective, unsafe, or not as described is unaffected by the fact that we do not accept change-of-mind returns.`,
        `If you are dissatisfied with how a claim has been handled, escalate it in writing to ${BUSINESS.email}. We acknowledge within 48 working hours and aim to resolve within 15 working days. You may also approach the National Consumer Helpline or the consumer forum having jurisdiction.`,
      ],
    },
  ];

  return (
    <PolicyPage
      title="Warranty & Returns Policy"
      currentPath="/warranty-returns"
      intro={`We do not accept change-of-mind returns. Faulty products are covered by the manufacturer's warranty, honoured through us as an authorised dealer. This page sets out exactly what is covered, what is not, and how to claim.`}
      sections={sections}
    />
  );
}
